/**
 * Version checking and cache invalidation utility
 *
 * Prevents deployment cache issues by:
 * 1. Fetching version.json on app load
 * 2. Comparing with stored version
 * 3. Clearing caches and reloading if version mismatch detected
 *
 * This ensures users see new code after deployment without force-refresh.
 */

interface VersionInfo {
  version: string
  buildDate: string
  gitCommit: string
  gitBranch: string
  nodeVersion: string
}

const VERSION_STORAGE_KEY = 'bgs-app-version'
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000 // Check every 5 minutes

/**
 * Check if a new version is available and reload if necessary
 */
export async function checkVersion(): Promise<boolean> {
  try {
    // Fetch version.json with cache-busting
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache'
      }
    })

    if (!response.ok) {
      console.warn('⚠️  version.json not found - version checking disabled')
      return false
    }

    // Check content type to avoid parsing HTML as JSON (dev server returns HTML for unknown routes)
    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      console.warn('⚠️  version.json not available (dev mode?) - version checking disabled')
      return false
    }

    const serverVersion: VersionInfo = await response.json()
    const clientVersion = localStorage.getItem(VERSION_STORAGE_KEY)

    console.log('📦 Version check:', {
      client: clientVersion || 'first-run',
      server: serverVersion.version,
      match: clientVersion === serverVersion.version
    })

    // First run or version mismatch
    if (clientVersion && clientVersion !== serverVersion.version) {
      console.log(`🔄 Version mismatch detected (${clientVersion} → ${serverVersion.version})`)
      console.log('🧹 Clearing caches and reloading...')

      await clearAllCaches()

      // Update stored version
      localStorage.setItem(VERSION_STORAGE_KEY, serverVersion.version)

      // Force reload with cache bypass
      window.location.href = `/?nocache=${Date.now()}`

      return true
    } else if (!clientVersion) {
      // First run - store version
      console.log('✅ First run - storing version:', serverVersion.version)
      localStorage.setItem(VERSION_STORAGE_KEY, serverVersion.version)
    }

    return false
  } catch (error) {
    console.error('❌ Version check failed:', error)
    return false
  }
}

/**
 * Clear all application caches
 */
async function clearAllCaches(): Promise<void> {
  try {
    // 1. Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map(reg => reg.unregister()))
      console.log(`✅ Unregistered ${registrations.length} service worker(s)`)
    }

    // 2. Clear Cache Storage API
    if ('caches' in window) {
      const cacheNames = await caches.keys()
      await Promise.all(cacheNames.map(name => caches.delete(name)))
      console.log(`✅ Cleared ${cacheNames.length} cache(s)`)
    }

    // 3. Clear session storage (but preserve version info in localStorage)
    sessionStorage.clear()
    console.log('✅ Cleared session storage')

  } catch (error) {
    console.error('❌ Failed to clear caches:', error)
  }
}

/**
 * Start periodic version checking
 */
export function startVersionCheckInterval(): void {
  // Initial check after a delay (avoid blocking initial render)
  setTimeout(() => {
    checkVersion()
  }, 2000)

  // Periodic checks
  setInterval(() => {
    checkVersion()
  }, VERSION_CHECK_INTERVAL)

  // Check when tab becomes visible
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      checkVersion()
    }
  })

  console.log('✅ Version checking enabled (interval: 5 minutes)')
}

/**
 * Manually trigger cache clear (for user-facing button)
 */
export async function manualCacheClear(): Promise<void> {
  console.log('🧹 Manual cache clear initiated')
  await clearAllCaches()

  // Force reload
  window.location.href = `/?nocache=${Date.now()}`
}

/**
 * Get current version info
 */
export function getCurrentVersion(): string | null {
  return localStorage.getItem(VERSION_STORAGE_KEY)
}

/**
 * Get build version from environment
 */
export function getBuildVersion(): string {
  return import.meta.env.VITE_BUILD_VERSION || 'unknown'
}
